import { useCallback, useEffect, useRef, useState } from 'react';

import type { NormalizedDevice } from '../types';

import { useDictationRuntime } from './useDictationRuntime';

export interface MicrophonePicker {
  /** False when the runtime offers no device list. */
  available: boolean;
  devices: NormalizedDevice[];
  loading: boolean;
  /** Null means the browser default (or nothing remembered yet). */
  selected: NormalizedDevice | null;
  select: (device: NormalizedDevice) => void;
  /** Reloads the device list; may prompt for permission when the user asked. */
  refresh: (options?: { interactive: boolean }) => Promise<void>;
}

/**
 * The microphone choice for dictation, shared with the encounter recorder
 * through the runtime's preference. Restores silently on mount (no prompt),
 * refreshes with a prompt when the menu opens, and follows hot-plug events.
 */
export function useMicrophones(): MicrophonePicker {
  const runtime = useDictationRuntime();
  const list = runtime?.listMicrophones;
  const preference = runtime?.microphonePreference;
  const [devices, setDevices] = useState<NormalizedDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<NormalizedDevice | null>(null);
  const mounted = useRef(true);

  const refresh = useCallback(
    async ({ interactive } = { interactive: false }) => {
      if (!list) return;
      setLoading(true);
      try {
        const found = await list({ interactive });
        if (!mounted.current) return;
        setDevices(found);
        setSelected(current => {
          const still = current && found.find(d => d.internalId === current.internalId);
          if (still) return still;
          return preference?.load(found) ?? null;
        });
      } catch {
        if (mounted.current) setDevices([]);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [list, preference],
  );

  const select = useCallback(
    (device: NormalizedDevice) => {
      setSelected(device);
      preference?.save(device);
    },
    [preference],
  );

  useEffect(() => {
    mounted.current = true;
    void refresh();
    const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    const onChange = () => void refresh();
    media?.addEventListener?.('devicechange', onChange);
    return () => {
      mounted.current = false;
      media?.removeEventListener?.('devicechange', onChange);
    };
  }, [refresh]);

  return { available: Boolean(list), devices, loading, selected, select, refresh };
}
